import {computed, onBeforeUnmount, Ref, ref, watch} from 'vue';
import {defineStore} from 'pinia';
import {I18nService} from '@malt/vue3-i18n';
import {HttpMethod} from '@malt/vue-context/src/types';
import {createEndpoint} from '@/logged/config';
import {AvailabilityFrequencyResource, AvailabilityResourceValue, ProfileAvailability} from '@/common/api/api-raw-types';
import {useGlobalProperties} from '@malt/vue3-use';
import {useUrlSearchParams} from '@/logged/features/header-menu/composables/useUrlSearchParams';
import {format} from 'date-fns';

export interface Availability {
    value: ProfileAvailability;
    frequency?: AvailabilityFrequencyResource;
    partial: boolean;
    nextAvailabilityDate?: string;
}

export type UpdateAvailability = {
    value: Extract<AvailabilityResourceValue, 'AVAILABLE' | 'NOT_AVAILABLE'>;
    frequency: AvailabilityFrequencyResource;
    nextAvailabilityDate?: Date;
};

export enum ComponentSelected {
    NONE = 'NONE',
    BOTTOM_SHEET = 'BOTTOM_SHEET',
    DIALOG = 'DIALOG',
    DROPDOWN = 'DROPDOWN',
}

type ShowUpdateAvailabilityDialog = {
    show: boolean;
    component: Extract<ComponentSelected, ComponentSelected.DIALOG | ComponentSelected.DROPDOWN | ComponentSelected.NONE>;
};

export const useAvailability = defineStore('availability', () => {
    const saveAvailabilityApi = createEndpoint<Availability, Omit<UpdateAvailability, 'nextAvailabilityDate'> & {nextAvailabilityDate?: string}>(
        'updateAvailability',
        {method: HttpMethod.PUT},
    );

    const availability: Ref<Availability | null> = ref(null);

    // @ts-ignore
    const updateAvailability: Ref<UpdateAvailability> = ref({});
    // @ts-ignore
    const showUpdateAvailabilityDialog: Ref<ShowUpdateAvailabilityDialog> = ref({
        show: false,
        component: ComponentSelected.NONE,
    });

    watch(availability, (newAvailability) => {
        updateAvailability.value = {
            value:
                newAvailability?.value === 'NOT_AVAILABLE' ||
                newAvailability?.value === 'NOT_AVAILABLE_WITH_DATE' ||
                window.location.hash === '#edit-availability-not-available'
                    ? 'NOT_AVAILABLE'
                    : 'AVAILABLE',
            frequency: newAvailability?.frequency ?? 'FULL_TIME',
            nextAvailabilityDate: newAvailability?.nextAvailabilityDate ? new Date(newAvailability?.nextAvailabilityDate) : undefined,
        };
    });

    const updateAvailabilityIsValid = computed(
        () =>
            updateAvailability.value.value === 'AVAILABLE' ||
            (updateAvailability.value.value === 'NOT_AVAILABLE' && updateAvailability.value.nextAvailabilityDate != undefined),
    );

    const {$heap} = useGlobalProperties();
    const {getParam} = useUrlSearchParams();
    function checkUrlParams() {
        const modifyAvailabilityParam = new URLSearchParams(window.location.search).get('modifyAvailability');
        if (window.location.hash.startsWith('#edit-availability') || modifyAvailabilityParam) {
            const source = getParam('source') ?? 'UNKNOWN';
            if (source === 'UNKNOWN') {
                console.warn('Unknown tracking availability source', document.referrer);
            }
            showAvailability(source, ComponentSelected.DIALOG);
        }
    }

    const showAvailabilityFromEvent = (source: string) => showAvailability(source, ComponentSelected.DROPDOWN);
    onBeforeUnmount(() => {
        Hop.events.off('navbar:setAvailability', showAvailabilityFromEvent);
    });
    Hop.events.on('navbar:setAvailability', showAvailabilityFromEvent);

    function showAvailability(
        source: string,
        component: Extract<ComponentSelected, ComponentSelected.DIALOG | ComponentSelected.DROPDOWN | ComponentSelected.NONE>,
    ) {
        showUpdateAvailabilityDialog.value = {show: true, component};
        $heap.track('open-modify-availability', {source});
    }

    async function saveAvailability() {
        const nextAvailabilityDateString = updateAvailability.value.nextAvailabilityDate
            ? format(updateAvailability.value.nextAvailabilityDate, 'yyyy-MM-dd')
            : undefined;
        availability.value = await saveAvailabilityApi({
            ...updateAvailability.value,
            nextAvailabilityDate: nextAvailabilityDateString,
        });
    }

    const label = computed(() => I18nService.icu('profile.availability.indicator.explicit.label.v2', {availability: availability.value?.value}));

    return {
        availability,
        updateAvailability,
        saveAvailability,
        updateAvailabilityIsValid,
        showAvailability,
        showUpdateAvailabilityDialog,
        checkUrlParams,
        label,
    };
});
